/**
 * The Chat mode of the admin Notifications composer: what the conversation looks like, as data.
 *
 * **Nothing here works.** The assistant behind Chat is not built; these are the design's seeded
 * conversations (`chatSeed` in the Notifications Screen export), kept so the screens can be seen
 * and reviewed. No part of this file calls anything, and nothing reads it but `ChatPanel` and the
 * design gallery.
 */

export interface SummaryRow {
  label: string;
  value: string;
}

export interface ChatChoice {
  id: string;
  label: string;
}

export interface ChatAction {
  id: string;
  label: string;
  variant: 'primary' | 'secondary' | 'quiet';
}

export interface ReviewRow {
  label: string;
  value: string;
}

/** The draft card: Title and Message as read-only values with their counters. */
export interface DraftPart {
  kind: 'draft';
  title: string;
  message: string;
  titleMax: number;
  messageMax: number;
  /** A photo is attached: the row with its thumbnail. */
  photo?: boolean;
}

/** Selectable chips, a radio-like group. */
export interface ChoicesPart {
  kind: 'choices';
  label: string;
  options: readonly ChatChoice[];
  selected?: string;
}

export interface ReviewPart {
  kind: 'review';
  rows: readonly ReviewRow[];
  reach: string;
  waves?: string;
}

export interface TestPart {
  kind: 'test';
  to: string;
  /** A test has been sent: the "I got the test" checkbox shows. */
  tested: boolean;
  confirmed: boolean;
  hint: string;
}

export interface ActionsPart {
  kind: 'actions';
  actions: readonly ChatAction[];
  /** Already answered: the buttons stay, spent. */
  answered?: boolean;
}

export type ChatPart = DraftPart | ChoicesPart | ReviewPart | TestPart | ActionsPart;

export type ChatPartKind = ChatPart['kind'];

export interface StaffMessage {
  id: string;
  from: 'staff';
  text: string;
  photo?: boolean;
}

export interface AssistantMessage {
  id: string;
  from: 'assistant';
  text?: string;
  parts?: readonly ChatPart[];
}

export type ChatMessage = StaffMessage | AssistantMessage;

/** Every kind of part, once. The panel's switch is checked against this by `describePart`. */
export const PART_KINDS: readonly ChatPartKind[] = ['draft', 'choices', 'review', 'test', 'actions'];

/** What a part is, in words: an exhaustive switch, so a new kind is a compile error here and in the panel. */
export function describePart(part: ChatPart): string {
  switch (part.kind) {
    case 'draft':
      return 'draft';
    case 'choices':
      return 'choices';
    case 'review':
      return 'review';
    case 'test':
      return 'test';
    case 'actions':
      return 'actions';
  }
}

/** The fields a summary may list, in the order it lists them. */
export const SUMMARY_FIELDS = ['Delivery', 'Audience', 'Title', 'Message', 'Opens to', 'Reason', 'Ticket'] as const;

export const DELIVERY_CHOICES: readonly ChatChoice[] = [
  { id: 'both', label: 'Push + inbox' },
  { id: 'push', label: 'Push only' },
  { id: 'inbox', label: 'Inbox only' },
];

const DRAFT_INTRO = "Here's a draft. Edit it below, or tell me what to change.";
const DENIM_TITLE = 'Denim Week is here';
const DENIM_MESSAGE = '40+ sellers, 3 days of shows. Tap to see what is live.';
const DENIM_ASK = 'Tell buyers who follow Denim that Denim Week is here: 40+ sellers, 3 days of shows';

const draft = (photo?: boolean): DraftPart => ({
  kind: 'draft',
  title: DENIM_TITLE,
  message: DENIM_MESSAGE,
  titleMax: 50,
  messageMax: 150,
  ...(photo ? { photo } : {}),
});

const AUDIENCE: SummaryRow = { label: 'Audience', value: 'Buyers who follow Denim (about 1,890)' };
const TITLE: SummaryRow = { label: 'Title', value: DENIM_TITLE };
const MESSAGE: SummaryRow = { label: 'Message', value: DENIM_MESSAGE };

export const CHAT_OPENING: readonly ChatMessage[] = [
  { id: 'c0', from: 'assistant', text: 'What do you want to send, and who should get it?' },
];

export const CHAT_FRAMES: readonly {
  id: string;
  label: string;
  messages: readonly ChatMessage[];
  summary: readonly SummaryRow[];
}[] = [
  {
    id: 'start',
    label: 'Chat',
    messages: [
      { id: 's1', from: 'staff', text: 'Tell denim followers about Denim Week' },
      {
        id: 'a1',
        from: 'assistant',
        text: 'Got it. I will send to Buyers who follow Denim (about 1,890 people). Look right?',
        parts: [
          {
            kind: 'actions',
            answered: true,
            actions: [
              { id: 'ok', label: 'Looks right', variant: 'primary' },
              { id: 'change', label: 'Change', variant: 'quiet' },
            ],
          },
        ],
      },
      { id: 's2', from: 'staff', text: 'Looks right' },
      {
        id: 'a2',
        from: 'assistant',
        text: 'How should it go out?',
        parts: [{ kind: 'choices', label: 'Delivery', options: DELIVERY_CHOICES }],
      },
    ],
    summary: [AUDIENCE],
  },
  {
    id: 'review',
    label: 'Chat review',
    messages: [
      { id: 'a1', from: 'assistant', text: DRAFT_INTRO, parts: [draft()] },
      { id: 's1', from: 'staff', text: 'Open the Denim Week kickoff show and send it now' },
      { id: 'a2', from: 'assistant', text: "What's the reason for this send, and which ticket is it tied to?" },
      { id: 's2', from: 'staff', text: 'Kick off Denim Week for denim followers, GO-412' },
      {
        id: 'a3',
        from: 'assistant',
        text: "Here's everything. Check the preview on the right.",
        parts: [
          {
            kind: 'review',
            rows: [
              { label: 'Delivery', value: 'Push + inbox' },
              { label: 'Audience', value: 'Buyers who follow Denim' },
              { label: 'Title', value: DENIM_TITLE },
              { label: 'Message', value: DENIM_MESSAGE },
              { label: 'Opens to', value: 'Denim Week kickoff show' },
              { label: 'When', value: 'Send now' },
              { label: 'Reason', value: 'Kick off Denim Week' },
              { label: 'Ticket', value: 'GO-412' },
            ],
            reach: 'About 1,890 people · 1,402 will receive a push notification',
            waves: 'Sent in waves over about 6 minutes',
          },
        ],
      },
      {
        id: 'a4',
        from: 'assistant',
        text: 'Send yourself a test before it goes out.',
        parts: [
          {
            kind: 'test',
            to: 'Me (Sophie S.)',
            tested: true,
            confirmed: false,
            hint: 'Send the test and confirm it first.',
          },
        ],
      },
      { id: 'a5', from: 'assistant', text: "Tap Send now when you're ready. I won't send from a typed reply." },
    ],
    summary: [
      { label: 'Delivery', value: 'Push + inbox' },
      AUDIENCE,
      TITLE,
      MESSAGE,
      { label: 'Opens to', value: 'Denim Week kickoff show' },
      { label: 'Reason', value: 'Kick off Denim Week' },
      { label: 'Ticket', value: 'GO-412' },
    ],
  },
  {
    id: 'unknown',
    label: 'Chat no matching segment',
    messages: [
      { id: 's1', from: 'staff', text: 'Tell people about Denim Week' },
      { id: 'a1', from: 'assistant', text: 'Who should get it?' },
      { id: 's2', from: 'staff', text: 'people who liked denim' },
      {
        id: 'a2',
        from: 'assistant',
        text: 'There is no option for likes. The closest is Buyers who follow Denim (1,890). Use that, or try a different segment?',
        parts: [
          {
            kind: 'actions',
            actions: [
              { id: 'use', label: 'Use that', variant: 'primary' },
              { id: 'other', label: 'Try another', variant: 'quiet' },
            ],
          },
        ],
      },
    ],
    summary: [],
  },
  {
    id: 'defaults',
    label: 'Chat only defaults missing',
    messages: [
      {
        id: 's1',
        from: 'staff',
        text: `${DENIM_ASK}. Reason: kick off Denim Week, GO-412`,
      },
      { id: 'a1', from: 'assistant', text: DRAFT_INTRO, parts: [draft()] },
      {
        id: 'a2',
        from: 'assistant',
        text: "I'll use Push + inbox, open to Home feed, and send now. OK, or change any?",
        parts: [
          {
            kind: 'actions',
            actions: [
              { id: 'ok', label: 'OK', variant: 'primary' },
              { id: 'change', label: 'Change', variant: 'quiet' },
            ],
          },
        ],
      },
    ],
    summary: [
      AUDIENCE,
      TITLE,
      MESSAGE,
      { label: 'Reason', value: 'Kick off Denim Week' },
      { label: 'Ticket', value: 'GO-412' },
    ],
  },
  {
    id: 'andor',
    label: 'Chat AND/OR read-back',
    messages: [
      { id: 's1', from: 'staff', text: 'Tell people about the new seller perks' },
      { id: 'a1', from: 'assistant', text: 'Who should get it?' },
      { id: 's2', from: 'staff', text: 'new sellers or new buyers' },
      {
        id: 'a2',
        from: 'assistant',
        text: 'Sellers who joined in the last 30 days, or Buyers who joined in the last 30 days · about 1,320 people. Look right?',
        parts: [
          {
            kind: 'actions',
            actions: [
              { id: 'ok', label: 'Looks right', variant: 'primary' },
              { id: 'change', label: 'Change', variant: 'quiet' },
            ],
          },
        ],
      },
    ],
    summary: [],
  },
  {
    id: 'photo',
    label: 'Chat photo attached',
    messages: [
      { id: 's1', from: 'staff', text: DENIM_ASK },
      { id: 'a1', from: 'assistant', text: DRAFT_INTRO, parts: [draft(true)] },
      { id: 's2', from: 'staff', text: 'Use this photo', photo: true },
      { id: 'a2', from: 'assistant', text: "Got it. I'll use this photo." },
    ],
    summary: [TITLE, MESSAGE],
  },
  {
    id: 'nophoto',
    label: 'Chat no photo',
    messages: [
      { id: 's1', from: 'staff', text: DENIM_ASK },
      { id: 'a1', from: 'assistant', text: DRAFT_INTRO, parts: [draft()] },
      { id: 'a2', from: 'assistant', text: "No photo, so I'll use the Going Once icon." },
    ],
    summary: [TITLE, MESSAGE],
  },
];
